// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Box, Grid, Link, Stack, Typography } from '@mui/material';

// icons
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';

// types
import { Invoice, InvoiceAttachment } from '../../../@types';

// utils
import { formatCurrency } from '../../../utils/currency';
import { formatDate } from '../../../utils/dates';

export interface CardInvoiceDetailsProps {
	invoice: Invoice;
	/**
	 * Resolves an attachment to a viewable file URL. In the EXT portal this
	 * maps the attachment's `documentId` to the matching AI document's
	 * `blob_url` (already fetched on the Contract page). When it returns
	 * undefined the attachment renders as a non-clickable file name.
	 */
	resolveFileUrl?: (attachment: InvoiceAttachment) => string | undefined;
	/**
	 * Render without the card's own border / header chrome so it can be
	 * embedded inside another bordered section (e.g. "Invoice for this
	 * milestone") without a nested card-in-card look. The header row (icon +
	 * "Invoice · num") still shows, just without the surrounding box.
	 */
	flat?: boolean;
}

export const CardInvoiceDetails = ({
	invoice,
	resolveFileUrl,
	flat = false,
}: CardInvoiceDetailsProps) => {
	const { t } = useTranslation();

	const amountLabel =
		invoice.amount != null
			? formatCurrency({ currency: invoice.currency, value: invoice.amount })
			: '';
	const taxLabel =
		invoice.tax != null && invoice.tax > 0
			? formatCurrency({ currency: invoice.currency, value: invoice.tax })
			: null;
	const dateLabel = invoice.date ? formatDate({ date: new Date(invoice.date) }) : null;
	const dateDueLabel = invoice.dateDue ? formatDate({ date: new Date(invoice.dateDue) }) : null;

	const attachments = invoice.attachments || [];

	const details: { label: string; value: string }[] = [];
	if (amountLabel) details.push({ label: t('amount'), value: amountLabel });
	if (taxLabel) details.push({ label: t('tax'), value: taxLabel });
	if (dateLabel) details.push({ label: t('invoiceDate'), value: dateLabel });
	if (dateDueLabel) details.push({ label: t('dateDue'), value: dateDueLabel });

	return (
		<Box
			sx={{
				border: flat ? 'none' : '1px solid',
				borderColor: 'divider',
				borderRadius: flat ? 0 : 1,
				bgcolor: flat ? 'transparent' : 'background.default',
				overflow: 'hidden',
			}}>
			<Stack
				direction="row"
				spacing={0.75}
				alignItems="center"
				sx={{
					px: 1.5,
					py: 1,
					bgcolor: flat ? 'transparent' : 'action.hover',
					borderBottom: flat ? 'none' : '1px solid',
					borderColor: 'divider',
				}}>
				<ReceiptLongIcon sx={{ fontSize: 20, color: 'primary.main' }} />
				<Typography sx={{ fontWeight: 700, color: 'text.primary', flex: 1 }}>
					{t('invoice')}
					{invoice.num ? ` · ${invoice.num}` : ''}
				</Typography>
			</Stack>

			<Box sx={{ p: 1.5 }}>
				{details.length > 0 && (
					<Grid container spacing={1.5} sx={{ mb: attachments.length ? 1.5 : 0 }}>
						{details.map((d) => (
							<Grid item xs={6} sm={3} key={d.label}>
								<Typography variant="caption" color="text.secondary" display="block">
									{d.label}
								</Typography>
								<Typography variant="body2" fontWeight={600}>
									{d.value}
								</Typography>
							</Grid>
						))}
					</Grid>
				)}

				{invoice.description && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mb: attachments.length ? 1.5 : 0 }}>
						{invoice.description}
					</Typography>
				)}

				{attachments.length > 0 && (
					<Stack spacing={0.5}>
						{attachments.map((att, idx) => {
							const url = resolveFileUrl ? resolveFileUrl(att) : undefined;
							const name = att.name || t('document');
							return (
								<Stack key={att.id ?? idx} direction="row" spacing={0.75} alignItems="center">
									<InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
									{url ? (
										<Link href={url} target="_blank" rel="noopener noreferrer" variant="body2">
											{name}
										</Link>
									) : (
										<Typography variant="body2">{name}</Typography>
									)}
								</Stack>
							);
						})}
					</Stack>
				)}
			</Box>
		</Box>
	);
};
